import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check, Sparkles, Globe, ShieldCheck, Clock } from "lucide-react";
import api from "../lib/api";
import { Reveal } from "../components/Reveal";
import ServiceIcon from "../components/ServiceIcon";
import RotatingWords from "../components/RotatingWords";
import { usePageMeta } from "../lib/seo";

const HERO_WORDS = ["cutting-edge AI", "Generative AI", "RAG systems", "AI agents", "smart automation", "data platforms"];

const TECH = [
  "Python", "FastAPI", "Django", "LangChain", "LangGraph", "GraphRAG", "Neo4j",
  "OpenAI", "Azure OpenAI", "Gemini", "Qdrant", "Pinecone", "Weaviate", "n8n",
  "Airflow", "Kafka", "Redis", "PostgreSQL", "MongoDB", "AWS", "Azure", "Docker", "Kubernetes",
];

export default function Home() {
  usePageMeta({
    title: "",
    description:
      "Best-in-class AI service provider building Generative AI, RAG/GraphRAG, chatbots, automation and data platforms — 10+ years of experience, serving India & worldwide.",
    path: "/",
  });
  const [services, setServices] = useState([]);
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({ years: "10+", projects: "20+", datasets: "500K+", clients_geo: "India & Global" });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/case-studies").then((r) => setCases(r.data.filter((c) => c.featured).slice(0, 3))).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden grid-bg">
        <div className="orb orb-1" style={{ width: 420, height: 420, top: -80, left: -60, background: "radial-gradient(circle, #0096DA 0%, transparent 70%)" }} />
        <div className="orb orb-2" style={{ width: 380, height: 380, top: 120, right: -40, background: "radial-gradient(circle, #003F7A 0%, transparent 70%)" }} />
        <div className="absolute inset-0 radial-glow pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-36 pb-24 sm:pt-44 sm:pb-32">
          <Reveal>
            <div className="inline-flex items-center gap-2 border border-white/10 glass px-4 py-1.5 rounded-full text-xs text-slate-300 mb-8">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              Best-in-class AI Service Provider · India & Worldwide
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-heading text-4xl sm:text-6xl lg:text-7xl font-semibold leading-[1.05] max-w-4xl text-white">
              We build <RotatingWords words={HERO_WORDS} /> that grows your business.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-7 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
              Production-grade Generative AI, RAG &amp; GraphRAG systems, intelligent chatbots, automation
              and data platforms — engineered by a team with 10+ years of experience, built for the long run.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid="hero-cta-button" className="group inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-7 py-4 glow-blue transition-all">
                Book a Free Consultation
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link to="/case-studies" data-testid="hero-secondary-button" className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white font-semibold px-7 py-4 transition-colors">
                View Our Work
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </Reveal>

          {/* Stats */}
          <Reveal delay={320}>
            <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
              {[
                { v: stats.years, l: "Years of experience" },
                { v: stats.projects, l: "Projects delivered" },
                { v: stats.datasets, l: "Datasets engineered" },
                { v: stats.clients_geo, l: "Clients served" },
              ].map((s, i) => (
                <div key={i} className="bg-[#0F111A] px-6 py-8">
                  <p className="font-heading text-3xl sm:text-4xl font-semibold text-white">{s.v}</p>
                  <p className="text-sm text-slate-400 mt-1.5">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* TECH MARQUEE */}
      <section className="border-y border-white/10 py-6 overflow-hidden">
        <div className="flex gap-10 w-max animate-marquee">
          {[...TECH, ...TECH].map((t, i) => (
            <span key={i} className="text-sm font-mono text-slate-500 whitespace-nowrap">{t}</span>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
        <Reveal>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-4">What we do</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold max-w-2xl">
              End-to-end AI engineering, from idea to production.
            </h2>
            <Link to="/services" className="inline-flex items-center gap-1.5 text-primary font-semibold hover:gap-2.5 transition-all shrink-0">
              All services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <Reveal key={s.slug} delay={i * 70}>
              <Link to="/services" data-testid={`home-service-${s.slug}`} className="group block h-full bg-[#0F111A]/80 border border-white/10 p-8 transition-all duration-300 hover:border-primary/50 hover:-translate-y-1">
                <div className="w-12 h-12 grid place-items-center border border-primary/30 bg-primary/10 text-primary mb-6 group-hover:glow-blue transition-all">
                  <ServiceIcon name={s.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white">{s.title}</h3>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed">{s.tagline}</p>
                <span className="inline-flex items-center gap-1.5 mt-6 text-sm text-primary font-medium">
                  Learn more <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED CASE STUDIES */}
      <section className="border-t border-white/10 bg-[#070810]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-4">Proven impact</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold max-w-2xl">
              Real AI products, delivering measurable results.
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {cases.map((c, i) => (
              <Reveal key={c.slug} delay={i * 90}>
                <Link to={`/case-studies/${c.slug}`} data-testid={`home-case-${c.slug}`} className="group block h-full border border-white/10 overflow-hidden bg-[#0F111A] hover:border-primary/50 transition-all duration-300">
                  <div className="relative h-44 overflow-hidden">
                    <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F111A] to-transparent" />
                    <span className="absolute top-4 left-4 text-xs bg-black/50 backdrop-blur border border-white/10 px-3 py-1 text-slate-200">{c.category}</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="font-heading text-2xl font-semibold text-primary">{c.metric_value}</span>
                      <span className="text-xs text-slate-400">{c.metric_label}</span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-white leading-snug">{c.title}</h3>
                    <p className="text-sm text-slate-400 mt-2 line-clamp-2">{c.summary}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-4">Why Growing IT Solutions</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              A long-term AI partner, not just a vendor.
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed">
              We obsess over quality and build relationships that last. Every engagement is led by senior
              engineers who understand both the AI and the business — so you ship faster and scale with confidence.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/about" className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white font-semibold px-6 py-3 transition-colors">
                About us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                { icon: ShieldCheck, t: "Senior-led delivery", d: "Architects with 10+ years on every project." },
                { icon: Globe, t: "India & global clients", d: "Trusted across geographies and time zones." },
                { icon: Sparkles, t: "Cutting-edge AI", d: "GraphRAG, agents, multimodal, automation." },
                { icon: Clock, t: "Long-term relationships", d: "We stay invested well beyond launch." },
              ].map((f, i) => (
                <div key={i} className="border border-white/10 bg-[#0F111A] p-6">
                  <f.icon className="w-7 h-7 text-primary mb-4" />
                  <h4 className="font-heading font-semibold text-white">{f.t}</h4>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
          <div className="relative overflow-hidden border border-primary/30 bg-gradient-to-br from-[#0F111A] to-[#05060A] p-12 sm:p-16 text-center grid-bg">
            <div className="absolute inset-0 radial-glow pointer-events-none" />
            <div className="relative">
              <h2 className="font-heading text-3xl sm:text-5xl font-semibold max-w-3xl mx-auto leading-tight">
                Ready to build something intelligent?
              </h2>
              <p className="mt-5 text-slate-400 max-w-xl mx-auto">
                Tell us about your goals and we'll map out an AI roadmap — no commitment required.
              </p>
              <Link to="/contact" data-testid="cta-contact-button" className="inline-flex items-center gap-2 mt-9 bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-4 glow-blue transition-all">
                Schedule a Consultation <ArrowUpRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
